import axios from 'utils/axios'
import { useMutation } from 'react-query'
import { queryClient } from 'queryClient'
import { guardFromErrors, extractErrorInfo } from 'utils/graphqlHelpers'
import { Campaign } from '../types'

/// ////////////////////////////////////////////////////////////////////////////////
// GRAPHQL
/// ////////////////////////////////////////////////////////////////////////////////

type QueryResponse = GraphQLResponse<'unarchiveCampaign', boolean>

const UNARCHIVE_CAMPAIGN = `
  mutation unarchiveCampaign($campaignId: Int!) {
    unarchiveCampaign(campaignId: $campaignId)
  }
`

/// ////////////////////////////////////////////////////////////////////////////////
// FUNCTION
/// ////////////////////////////////////////////////////////////////////////////////

type UnarchiveCampaign = (campaignId: number) => Promise<number>

const unarchiveCampaign: UnarchiveCampaign = async (campaignId) => {
  try {
    const {
      data: { data, errors }
    }: QueryResponse = await axios.post('/graphql', {
      query: UNARCHIVE_CAMPAIGN,
      variables: { campaignId }
    })

    guardFromErrors(errors)

    if (!data) {
      throw new Error('Response body is empty!')
    }

    const { unarchiveCampaign } = data
    if (!unarchiveCampaign) {
      throw new Error("Can't unarchive campaign!")
    }

    return campaignId
  } catch (err) {
    throw new Error(extractErrorInfo(err) as string)
  }
}

/// ////////////////////////////////////////////////////////////////////////////////
// HOOK EVENTS
/// ////////////////////////////////////////////////////////////////////////////////

type UnarchiveCampaignContext = { prevCampaign?: Campaign }

type UnarchiveCampaignEvents = {
  onMutate: (campaignId: number) => Promise<UnarchiveCampaignContext>
  onError: (error: string, campaignId: number, context: UnarchiveCampaignContext) => void
  onSettled: (campaignId?: number) => void
}

const unarchiveCampaignEvents: UnarchiveCampaignEvents = {
  onMutate: async (campaignId) => {
    await queryClient.cancelQueries(['campaign', campaignId])

    // Snapshot the previous value
    const prevCampaign = queryClient.getQueryData<Campaign>(['campaign', campaignId])

    if (prevCampaign) {
      queryClient.removeQueries(['campaign', campaignId])
    }

    return { prevCampaign }
  },
  onError: (_err, campaignId, context) => {
    if (context?.prevCampaign) {
      // Restore campaign on any error
      queryClient.setQueryData<Campaign>(['campaign', campaignId], context.prevCampaign)
    }
  },
  onSettled: (campaignId: number) => {
    if (campaignId) {
      queryClient.invalidateQueries(['campaign', campaignId])
      queryClient.invalidateQueries(['campaignsList'])
    }
  }
}

/// ////////////////////////////////////////////////////////////////////////////////
// HOOK
/// ////////////////////////////////////////////////////////////////////////////////

export const useUnarchiveCampaign = () => useMutation(unarchiveCampaign, unarchiveCampaignEvents)
